<template>
	<div id="simple-calendar-app">
		<div class="vx-card no-scroll-content no-shadow card-border" style="height: 81vh">
			<calendar-view
				ref="calendar"
				enableDragDrop
				eventBorderHeight="0px"
				eventContentHeight="1.65rem"
				class="theme-default"
				:displayPeriodUom="calendarView"
				:show-date="showDate"
				:events="simpleCalendarEvents"
				:eventTop="windowWidth <= 400 ? '2rem' : '3rem'"
				:locale="calendarLocale"
				:doEmitItemMouseEvents="true"
				@item-mouseenter="mouseEnter"
				@item-mouseleave="mouseLeave"
			>
				<div slot="header" class="mb-4">
					<div class="vx-row no-gutter">
						<div class="vx-col sm:w-1/1 w-full">
							<feather-icon
								class="float-right"
								v-if="isMaximize"
								svgClasses="w-7 h-7 hover:text-primary stroke-current cursor-pointer"
								:icon="maximize ? 'MinimizeIcon' : 'MaximizeIcon'"
								:title="maximize ? $t('Minimize') : $t('Maximize')"
								@click.stop="maximizeCalendar"
							/>
						</div>
						<!-- Current Month -->
						<div class="vx-col sm:w-1/2 w-full sm:my-0 my-3 flex sm:justify-end justify-center order-last">
							<div class="flex items-center">
								<feather-icon
									:icon="$vs.rtl ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
									@click="updateMonth(-1)"
									svgClasses="w-5 h-5 m-1"
									class="cursor-pointer bg-primary text-white rounded-full"
								/>
								<span class="mx-3 text-xl font-medium whitespace-no-wrap">{{ showDate | month }}</span>
								<feather-icon
									:icon="$vs.rtl ? 'ChevronLeftIcon' : 'ChevronRightIcon'"
									@click="updateMonth(1)"
									svgClasses="w-5 h-5 m-1"
									class="cursor-pointer bg-primary text-white rounded-full"
								/>
							</div>
						</div>
						<div class="vx-col sm:w-1/2 w-full flex justify-start">
							<template v-for="(view, index) in calendarViewTypes">
								<vs-button
									v-if="calendarView === view.val"
									:key="String(view.val) + 'filled'"
									type="filled"
									class="p-3 md:px-8 md:py-3"
									:class="{
										'border-l-0 rounded-l-none': index,
										'rounded-r-none': calendarViewTypes.length !== index + 1,
									}"
									@click="calendarView = view.val"
									>{{ view.label }}</vs-button
								>
								<vs-button
									v-else
									:key="String(view.val) + 'border'"
									type="border"
									class="p-3 md:px-8 md:py-3"
									:class="{
										'border-l-0 rounded-l-none': index,
										'rounded-r-none': calendarViewTypes.length !== index + 1,
									}"
									@click="calendarView = view.val"
									>{{ view.label }}</vs-button
								>
							</template>
						</div>
					</div>
				</div>
			</calendar-view>
		</div>
	</div>
</template>

<script>
import {CalendarView, CalendarViewHeader} from "vue-simple-calendar";
import moduleTimeWindow from "@/store/time-window/moduleTimeWindow.js";
require("vue-simple-calendar/static/css/default.css");
import Datepicker from "vuejs-datepicker";
import {en, es} from "vuejs-datepicker/src/locale";
import DateUtils from "@/helpers/DateUtils.js";
import moment from "moment";

export default {
	components: {
		CalendarView,
		CalendarViewHeader,
		Datepicker,
	},
	props: {
		duration: {
			type: String,
			default: "00:00:00",
		},
		cronExpression: {
			type: String,
			default: "* * * * *",
		},
		isMaximize: {
			type: Boolean,
			default: true,
		},
		viewData: {
			type: Boolean,
			default: false,
		},
		isSoftTimeWindow: {
			type: Boolean,
			default: false,
		},
		softTime: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			showDate: new Date(),
			numberDaysInMont: 30,
			disabledFrom: false,
			id: 0,
			title: "",
			startDate: "",
			endDate: "",
			labelLocal: "none",
			langEs: es,
			langEn: en,
			url: "",
			calendarView: "month",
			maximize: false,
			calendarViewTypes: [
				{
					label: this.$t("TimeWindow.Month"),
					val: "month",
				},
				{
					label: this.$t("TimeWindow.Week"),
					val: "week",
				},
			],
		};
	},
	computed: {
		simpleCalendarEvents() {
			if (!this.$store.state.moduleTimeWindow.eventsTimeWindow) {
				return [];
			}
			const events = this.$store.state.moduleTimeWindow.eventsTimeWindow;
			return events.map((event, index) => {
				const isRange = !moment(event.init_date_window).isSame(event.end_date_window, "day");
				const dateInit = new Date(event.init_date_window);
				const parseDateInit = dateInit.setDate(dateInit.getDate() + 1);
				const dateEnd = new Date(event.end_date_window);
				const parseDateEnd = dateEnd.setDate(dateEnd.getDate() + 1);
				const hasSoftTimeWindow = event.hasOwnProperty("soft_time_window") && event.soft_time_window != null;
				const timeWindowTitle = `${this.$t("TimeWindow.TimeWindow")}: ${event.init_hour_window} - ${
					event.end_hour_window
				}`;
				const softTimeWindowTitle = hasSoftTimeWindow
					? `${this.$t("TimeWindow.SoftTimeWindow")}: \n ${event.soft_time_window.init_date} - ${
							event.soft_time_window.end_date
					  } \n ${event.soft_time_window.init_hour} - ${event.soft_time_window.end_hour}`
					: "";
				const generateTitle = () => {
					if (hasSoftTimeWindow && this.maximize) {
						return `
						<div title="${timeWindowTitle}\n${softTimeWindowTitle}">
						<span>${event.init_hour_window} - ${event.end_hour_window}</span>
						<div id="timewindow${index}" title="${softTimeWindowTitle}" class="vx-card"
							style="position:relative; display:none; background-color:white; font-size:small; color:gray; z-index:999999;
							width:100%; height:auto; border-radius:5px; padding:8px; margin-top:2px;">
							<small class="font-semibold mb-1">${this.$t("TimeWindow.SoftTimeWindow")}</small><br/>
							<small>${event.soft_time_window.init_date} - ${event.soft_time_window.end_date}</small><br/>
							<small>${event.soft_time_window.init_hour} - ${event.soft_time_window.end_hour}</small>
						</div>
						</div>`;
					} else if (hasSoftTimeWindow && !this.maximize) {
						return `
						<div title="${timeWindowTitle}\n${softTimeWindowTitle}">
							<span>${event.init_hour_window} - ${event.end_hour_window}</span>
						</div>`;
					} else {
						return `${event.init_hour_window} - ${event.end_hour_window}`;
					}
				};
				return {
					id: `timewindow${index}`,
					title: generateTitle(),
					startDate: parseDateInit,
					endDate: parseDateEnd,
					url: "",
					classes: `event-item ${isRange ? "range-event" : "single-day-event"}`,
					label: "",
				};
			});
		},
		validForm() {
			return (
				this.title !== "" &&
				this.startDate !== "" &&
				this.endDate !== "" &&
				Date.parse(this.endDate) - Date.parse(this.startDate) >= 0 &&
				!this.errors.has("event-url")
			);
		},
		windowWidth() {
			return this.$store.state.windowWidth;
		},
		dateInitComputed: {
			get() {
				const date = new Date(this.showDate);
				date.setMonth(date.getMonth() - 1);
				date.setDate(15);
				return DateUtils.toDefaultDateTime(date, "YYYY-MM-DD");
			},
			set(val) {},
		},
		dateFinalComputed: {
			get() {
				// Convertir `dateInitComputed` en un objeto de fecha
				const initialDate = new Date(this.dateInitComputed);
				// Obtener el mes siguiente
				const nextMonth = initialDate.getMonth() + 2;
				// Crear una nueva fecha establecida para el día 15 del próximo mes
				const finalDate = new Date(initialDate.getFullYear(), nextMonth, 15);
				// Devolver la fecha final en el formato "YYYY-MM-DD"
				return DateUtils.toDefaultDateTime(finalDate, "YYYY-MM-DD");
			},
			set(val) {},
		},
		calendarLocale() {
			return this.$store.state.localeApp;
		},
	},
	created() {
		if (!moduleTimeWindow.isRegistered) {
			this.$store.registerModule("moduleTimeWindow", moduleTimeWindow);
			moduleTimeWindow.isRegistered = true;
		}
	},
	mounted() {
		this.showDate = new Date(this.showDate.getFullYear(), this.showDate.getMonth(), 1);
		this.numberDaysInMont = DateUtils.getNumberOfDaysInSeletedMonth(
			this.showDate.getMonth() + 1,
			this.showDate.getFullYear()
		);
		this.maximize = !this.isMaximize;
		if (this.viewData) this.getEvents();
	},
	methods: {
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		maximizeCalendar() {
			this.maximize = !this.maximize;
			this.$emit("maximizeEmit", this.maximize);
		},
		updateMonth(val) {
			this.showDate = this.$refs.calendar.getIncrementedPeriod(val);
			this.updateComputedDates();
			this.$emit("showDateEmit", this.showDate);
			this.getEvents();
		},
		updateComputedDates() {
			// Calculate number of days for current month
			this.numberDaysInMont = DateUtils.getNumberOfDaysInSeletedMonth(
				this.showDate.getMonth() + 1,
				this.showDate.getFullYear()
			);
			const dateInit = DateUtils.toDefaultDateTime(this.showDate, "YYYY-MM-DD");
			const dateFinalMilisecods = this.addDaysToDate(dateInit, this.numberDaysInMont);
			const dateFinal = DateUtils.toDefaultDateTime(new Date(dateFinalMilisecods), "YYYY-MM-DD");
			// Update computeds properties
			this.dateInitComputed = dateInit;
			this.dateFinalComputed = dateFinal;
		},
		clearFields() {
			this.title = this.endDate = this.url = "";
			this.id = 0;
			this.labelLocal = "none";
		},
		addDaysToDate(stringDate, dayNums) {
			const date = new Date(stringDate);
			return date.setDate(date.getDate() + dayNums);
		},
		getEvents() {
			const payload = {
				init_date: this.dateInitComputed,
				end_date: this.dateFinalComputed,
				end_hour: "23:59:59",
				init_hour: "00:00:00",
				time_windows: [
					{
						cron_exp: this.cronExpression,
						duration: this.duration,
						...(this.isSoftTimeWindow
							? {
									soft_time_window: {
										before_soft_duration: this.softTime.before_soft_duration,
										after_soft_duration: this.softTime.after_soft_duration,
									},
							  }
							: {}),
					},
				],
			};
			this.openLoading();
			this.$store
				.dispatch("moduleTimeWindow/findTimeWindowSimulate", payload)
				.then((res) => {
					if (res.data[0] === "invalid cron expression") {
						this.$vs.notify({
							color: "warning",
							title: this.$t("TimeWindow.Schedule"),
							text: this.$t("TimeWindow.TheChosenTimeIsNotValid"),
							time: 7000,
						});
					}
					this.closeLoading();
				})
				.catch((err) => {
					this.closeLoading();
					console.error(err);
				});
		},
		mouseEnter(item) {
			if (document.getElementById(item.id) && this.maximize)
				document.getElementById(item.id).style.display = "block";
		},
		mouseLeave(item) {
			if (document.getElementById(item.id) && this.maximize)
				document.getElementById(item.id).style.display = "none";
		},
	},
	beforeDestroy() {
		this.$store.unregisterModule("calendar");
	},
};
</script>

<style lang="scss">
@import "@/assets/scss/vuexy/apps/simple-calendar.scss";
#simple-calendar-app {
	font-family: "Avenir", Helvetica, Arial, sans-serif;
	color: #2c3e50;
	font-size: 11px !important;
	/* Estilos para eventos de rango */
	.range-event {
		background-color: #3788d8; /* Color para eventos de varios días */
		color: white;
		padding: 2px;
		border-radius: 4px;
		font-size: 1em;
		text-align: center;
		margin-left: 5px !important;
	}

	/* Estilos para eventos de un solo día */
	.single-day-event {
		background-color: #4caf50; /* Color para eventos de un solo día */
		color: white;
		padding: 2px;
		border-radius: 4px;
		font-size: 1em;
	}
	.cv-event.span2 {
		width: calc(28.57143% - 1em);
	}
}
</style>
